import { forwardRef } from "react";
import { stripNikud } from "../lib/api";

/**
 * A single printed page inside the 3D book.
 * Variants:
 *   - "text"           — verses printed
 *   - "chapter-title"  — decorative full-page chapter opener
 *   - "toc"            — parchment-style table of contents (receives tocContent)
 *   - "transition"     — elegant page offering to load the next/prev chapter
 *                        (props: transitionDirection = "next" | "prev",
 *                                onTransitionClick, transitionLabelHe)
 *   - "blank"          — decorative blank page
 *
 * Forwarded ref is required by react-pageflip.
 */
const BookPage = forwardRef(function BookPage(
  {
    variant = "text",
    bookHe,
    bookEn,
    chapter,
    pageIndex,
    side = "right",
    verses = [],
    startsChapter = false,
    activeVerseNum = null,
    onVerseClick,
    highlights = {},
    notesByVerse = {},
    nikud = true,
    showVerseNums = true,
    tocContent,       // ReactNode for "toc" variant
    chapterTitle,     // explicit title for chapter-title variant
    transitionDirection, // "next" | "prev" (transition variant)
    onTransitionClick,
    transitionLabelHe,
    transitionTargetChapter,
    transitionTargetChapterHe, // pre-formatted Hebrew label (e.g. "דף ב׳ ע״ב")
    chapterLabelHe,            // pre-formatted Hebrew label (e.g. "דף ב׳ ע״א")
    chapterLabelEn,            // pre-formatted English label (e.g. "Daf 2a")
  },
  ref
) {
  const render = (text) => (nikud ? text : stripNikud(text));

  return (
    <div ref={ref} className="book-page-paper" data-density="hard">
      <div
        className={`book-page-inner page-${side}`}
        style={{ position: "absolute", inset: 0 }}
      >
        {variant === "chapter-title" && (
          <div className="flex h-full flex-col items-center justify-center px-10 text-center">
            <p className="font-latin italic text-xs tracking-[0.35em] uppercase text-[hsl(36,75%,38%)] mb-4">
              {bookEn} · {chapterLabelEn || `Chapter ${chapter}`}
            </p>
            <h2 className="font-heading text-6xl" style={{ color: "inherit" }}>
              {chapterTitle || bookHe}
            </h2>
            <div className="book-chapter-ornament mt-4 w-32">✦</div>
            <p className="font-heading text-2xl mt-3 text-[hsl(36,75%,38%)]">
              {chapterLabelHe || `פרק ${numberToHebrew(chapter)}`}
            </p>
          </div>
        )}

        {variant === "toc" && tocContent}

        {variant === "text" && (
          <>
            <div className="book-page-header">
              <span className="book-header-title">{bookHe}</span>
              <span className="book-header-num">
                {bookEn} · {chapterLabelEn || chapter}
              </span>
            </div>

            {startsChapter && (
              <div className="book-chapter-title">
                <h3 className="he">{chapterLabelHe || `פרק ${numberToHebrew(chapter)}`}</h3>
                <div className="book-chapter-ornament">✦</div>
              </div>
            )}

            <div
              className={`book-printed-verses ${startsChapter ? "book-printed-dropcap" : ""}`}
              dir="rtl"
            >
              {verses.map((v, idx) => {
                const hl = highlights[v.verse];
                const note = notesByVerse[v.verse];
                const active = activeVerseNum === v.verse;
                // Stop touch/pointer/mouse events from bubbling up to the
                // flipbook gesture engine so tapping a verse never starts
                // a page-flip swipe. The verse itself handles its own
                // click and lets text-selection work natively.
                const stop = (e) => e.stopPropagation();
                return (
                  <span
                    key={v.verse}
                    data-testid={`book-verse-${v.verse}`}
                    onPointerDown={stop}
                    onMouseDown={stop}
                    onTouchStart={stop}
                    onClick={(e) => {
                      e.stopPropagation();
                      onVerseClick?.(v);
                    }}
                    className={`book-printed-verse ${active ? "is-active" : ""} ${hl ? `hl-${hl.color}` : ""}`}
                    data-ref={`${bookEn} ${chapter}:${v.verse}`}
                  >
                    {showVerseNums && (
                      <span className="book-printed-verse-num">
                        {numberToHebrew(v.verse)}
                      </span>
                    )}
                    <span>{render(v.he)}</span>
                    {note && <span className="text-[hsl(36,75%,38%)]">◆</span>}
                    {idx < verses.length - 1 ? " " : ""}
                  </span>
                );
              })}
            </div>

            <div className="book-page-footer">
              <span className="book-page-folio">{pageIndex + 1}</span>
            </div>
          </>
        )}

        {variant === "blank" && (
          <div className="flex h-full items-center justify-center">
            <div className="text-[hsl(36,60%,40%)] opacity-50 text-3xl">✦</div>
          </div>
        )}

        {variant === "transition" && (
          <div
            className="flex h-full flex-col items-center justify-center px-8 text-center gap-5"
            data-testid={`book-transition-${transitionDirection}`}
          >
            <p className="font-latin italic text-[11px] tracking-[0.35em] uppercase text-[hsl(36,75%,38%)]">
              {transitionDirection === "next" ? "Continue reading" : "Previous chapter"}
            </p>
            <div className="book-chapter-ornament w-40">
              ✦ ❖ ✦
            </div>
            <h2
              className="font-heading text-3xl md:text-4xl leading-tight"
              style={{ color: "inherit" }}
            >
              {transitionLabelHe}
            </h2>
            {transitionTargetChapter != null && (
              <p className="font-heading text-xl text-[hsl(36,75%,38%)]">
                {bookHe} · {transitionTargetChapterHe || `פרק ${numberToHebrew(transitionTargetChapter)}`}
              </p>
            )}
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onTransitionClick?.();
              }}
              data-testid={`book-transition-btn-${transitionDirection}`}
              className="mt-3 inline-flex items-center gap-2 rounded-sm border border-[hsl(36,75%,38%)] bg-[hsl(36,75%,38%)]/10 px-5 py-2 text-sm font-heading text-[hsl(36,75%,38%)] hover:bg-[hsl(36,75%,38%)]/25 transition-colors"
            >
              {transitionDirection === "next" ? (transitionLabelHe || "המשך לפרק הבא") : "חזור לפרק הקודם"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

function numberToHebrew(n) {
  if (!n || n < 1) return "";
  const map = [
    [400, "ת"], [300, "ש"], [200, "ר"], [100, "ק"],
    [90, "צ"], [80, "פ"], [70, "ע"], [60, "ס"], [50, "נ"], [40, "מ"], [30, "ל"], [20, "כ"], [10, "י"],
    [9, "ט"], [8, "ח"], [7, "ז"], [6, "ו"], [5, "ה"], [4, "ד"], [3, "ג"], [2, "ב"], [1, "א"],
  ];
  let remaining = n;
  let out = "";
  for (const [val, ch] of map) {
    while (remaining >= val) {
      out += ch;
      remaining -= val;
    }
  }
  out = out.replace("יה", "טו").replace("יו", "טז");
  if (out.length > 1) {
    out = out.slice(0, -1) + '״' + out.slice(-1);
  } else if (out.length === 1) {
    out = out + "׳";
  }
  return out;
}

export default BookPage;
